import {
  cn,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassInput,
  GlassSelect,
  GlassTabs,
  GlassTabPanel,
} from '@merritrac/shared/components';
import {
  ExternalLink,
  Globe,
  KeyRound,
  Mail,
  MapPin,
  PlugZap,
  RefreshCw,
  Settings,
  ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';

import type { ReactElement } from 'react';

/* ── Constants ── */

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'security', label: 'Security' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
] as const;

const DATE_FORMAT_OPTIONS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
  { value: 'MMM D, YYYY', label: 'Jan 1, 2026' },
] as const;

const FISCAL_MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
] as const;

const SESSION_TIMEOUT_OPTIONS = [
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '240', label: '4 hours' },
] as const;

/* ── Toggle Row ── */

interface ToggleRowProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleRow({ id, label, description, checked, onChange }: ToggleRowProps): ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-[var(--glass-border-subtle)] last:border-b-0">
      <div className="flex-1 min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-text cursor-pointer select-none">
          {label}
        </label>
        <p className="text-sm text-text-tertiary mt-0.5">{description}</p>
      </div>
      <button
        id={id}
        role="switch"
        type="button"
        aria-checked={checked}
        onClick={(): void => {
          onChange(!checked);
        }}
        className={cn(
          'relative flex-shrink-0 mt-0.5 w-11 h-6 rounded-full transition-colors cursor-pointer',
          'duration-[var(--dur)] ease-[var(--ease)]',
          'focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2',
          checked
            ? 'bg-primary'
            : 'bg-[var(--glass-bg-strong)] border border-[var(--glass-border)]',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm',
            'transition-transform duration-[var(--dur)] ease-[var(--ease)]',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </button>
    </div>
  );
}

/* ── Integration Card ── */

type IntegrationStatus = 'connected' | 'disconnected';

interface IntegrationCardProps {
  icon: ReactElement;
  name: string;
  description: string;
  status: IntegrationStatus;
  apiKeyMasked?: string;
  onConfigure: () => void;
  onConnect?: () => void;
}

function IntegrationCard({
  icon,
  name,
  description,
  status,
  apiKeyMasked,
  onConfigure,
  onConnect,
}: IntegrationCardProps): ReactElement {
  const isConnected = status === 'connected';
  return (
    <GlassCard hover>
      <GlassCardContent className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg glass flex-shrink-0">
              {icon}
            </div>
            <div>
              <p className="text-sm font-semibold text-text">{name}</p>
              <p className="text-xs text-text-tertiary mt-0.5">{description}</p>
            </div>
          </div>
          <GlassBadge variant={isConnected ? 'success' : 'default'} dot>
            {isConnected ? 'Connected' : 'Not connected'}
          </GlassBadge>
        </div>
        {isConnected && apiKeyMasked !== undefined && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-text-secondary">API Key</label>
            <div className="h-9 px-3 rounded-lg glass text-text-tertiary text-sm flex items-center font-mono tracking-wider">
              {apiKeyMasked}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {isConnected ? (
            <GlassButton variant="secondary" size="sm" onClick={onConfigure}>
              <Settings size={13} /> Configure
            </GlassButton>
          ) : (
            <GlassButton variant="primary" size="sm" onClick={onConnect}>
              <PlugZap size={13} /> Connect
            </GlassButton>
          )}
          <GlassButton variant="ghost" size="sm">
            <ExternalLink size={13} /> Docs
          </GlassButton>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

/* ── Section Header ── */

function SectionHeader({
  icon,
  title,
  description,
}: {
  icon: ReactElement;
  title: string;
  description: string;
}): ReactElement {
  return (
    <div className="flex items-start gap-3 mb-6">
      <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-soft flex-shrink-0 mt-0.5">
        <span className="text-primary">{icon}</span>
      </div>
      <div>
        <h2 className="text-base font-semibold text-text">{title}</h2>
        <p className="text-sm text-text-tertiary mt-0.5">{description}</p>
      </div>
    </div>
  );
}

/* ── General Tab ── */

interface GeneralState {
  companyName: string;
  timezone: string;
  dateFormat: string;
  fiscalYearStart: string;
}

function GeneralTab(): ReactElement {
  const [form, setForm] = useState<GeneralState>({
    companyName: 'MerriTrac Field Services',
    timezone: 'America/Chicago',
    dateFormat: 'MM/DD/YYYY',
    fiscalYearStart: '1',
  });
  const handleChange =
    (field: keyof GeneralState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };
  return (
    <GlassTabPanel>
      <SectionHeader
        icon={<Globe size={16} />}
        title="General Settings"
        description="Configure your organization's core preferences and regional settings."
      />
      <GlassCard>
        <GlassCardContent className="flex flex-col gap-5 py-5">
          <GlassInput
            label="Company Name"
            value={form.companyName}
            onChange={handleChange('companyName')}
            placeholder="Enter company name"
          />
          <GlassSelect
            label="Timezone"
            options={TIMEZONE_OPTIONS}
            value={form.timezone}
            onChange={handleChange('timezone')}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <GlassSelect
              label="Default Date Format"
              options={DATE_FORMAT_OPTIONS}
              value={form.dateFormat}
              onChange={handleChange('dateFormat')}
            />
            <GlassSelect
              label="Fiscal Year Start Month"
              options={FISCAL_MONTH_OPTIONS}
              value={form.fiscalYearStart}
              onChange={handleChange('fiscalYearStart')}
            />
          </div>
        </GlassCardContent>
      </GlassCard>
      <div className="mt-4 flex justify-end">
        <GlassButton variant="primary" size="md">
          Save Changes
        </GlassButton>
      </div>
    </GlassTabPanel>
  );
}

/* ── Notifications Tab ── */

interface NotificationState {
  emailNewOrders: boolean;
  smsOverdueOrders: boolean;
  dailySummary: boolean;
  weeklyPerformance: boolean;
}

function NotificationsTab(): ReactElement {
  const [prefs, setPrefs] = useState<NotificationState>({
    emailNewOrders: true,
    smsOverdueOrders: true,
    dailySummary: false,
    weeklyPerformance: true,
  });
  const toggle =
    (field: keyof NotificationState) =>
    (checked: boolean): void => {
      setPrefs((prev) => ({ ...prev, [field]: checked }));
    };
  return (
    <GlassTabPanel>
      <SectionHeader
        icon={<Mail size={16} />}
        title="Notification Preferences"
        description="Choose which events trigger alerts and how you want to be notified."
      />
      <GlassCard>
        <GlassCardContent className="py-2">
          <ToggleRow
            id="notif-email-new-orders"
            label="Email notifications on new orders"
            description="Receive an email whenever a new service order is created."
            checked={prefs.emailNewOrders}
            onChange={toggle('emailNewOrders')}
          />
          <ToggleRow
            id="notif-sms-overdue"
            label="SMS alerts for overdue orders"
            description="Get a text message when a service order passes its scheduled completion time."
            checked={prefs.smsOverdueOrders}
            onChange={toggle('smsOverdueOrders')}
          />
          <ToggleRow
            id="notif-daily-summary"
            label="Daily summary reports"
            description="Receive a morning digest with yesterday's completed orders and technician utilization."
            checked={prefs.dailySummary}
            onChange={toggle('dailySummary')}
          />
          <ToggleRow
            id="notif-weekly-performance"
            label="Weekly performance reports"
            description="Get a detailed weekly report covering KPIs, trends, and team performance."
            checked={prefs.weeklyPerformance}
            onChange={toggle('weeklyPerformance')}
          />
        </GlassCardContent>
      </GlassCard>
    </GlassTabPanel>
  );
}

/* ── Integrations Tab ── */

function IntegrationsTab(): ReactElement {
  return (
    <GlassTabPanel>
      <SectionHeader
        icon={<PlugZap size={16} />}
        title="Integrations"
        description="Connect MerriTrac with external services to extend platform capabilities."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <IntegrationCard
          icon={<MapPin size={18} className="text-text-secondary" />}
          name="Google Maps API"
          description="Real-time routing and location services for field technicians."
          status="connected"
          apiKeyMasked="AIza\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022KXYZ"
          onConfigure={(): void => {
            /* noop */
          }}
        />
        <IntegrationCard
          icon={<Mail size={18} className="text-text-secondary" />}
          name="Email Service"
          description="SendGrid powers transactional and notification emails."
          status="connected"
          apiKeyMasked="SG.\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
          onConfigure={(): void => {
            /* noop */
          }}
        />
        <IntegrationCard
          icon={<RefreshCw size={18} className="text-text-secondary" />}
          name="Accounting Software"
          description="Sync invoices, payments, and job costs with your accounting system."
          status="disconnected"
          onConfigure={(): void => {
            /* noop */
          }}
          onConnect={(): void => {
            /* noop */
          }}
        />
      </div>
    </GlassTabPanel>
  );
}

/* ── Security Tab ── */

interface SecurityState {
  minPasswordLength: string;
  requireSpecialChars: boolean;
  sessionTimeout: string;
  twoFactorEnabled: boolean;
}

function SecurityTab(): ReactElement {
  const [form, setForm] = useState<SecurityState>({
    minPasswordLength: '12',
    requireSpecialChars: true,
    sessionTimeout: '60',
    twoFactorEnabled: false,
  });
  return (
    <GlassTabPanel>
      <SectionHeader
        icon={<ShieldCheck size={16} />}
        title="Security Settings"
        description="Control authentication policies, session behavior, and access security."
      />
      <div className="flex flex-col gap-4">
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-2">
              <KeyRound size={15} className="text-text-secondary" />
              <h3 className="text-sm font-semibold text-text">Password Policy</h3>
            </div>
          </GlassCardHeader>
          <GlassCardContent className="flex flex-col gap-5 py-5">
            <div className="max-w-xs">
              <GlassInput
                label="Minimum password length"
                type="number"
                min="6"
                max="32"
                value={form.minPasswordLength}
                onChange={(e): void => {
                  setForm((prev) => ({ ...prev, minPasswordLength: e.target.value }));
                }}
              />
              <p className="text-xs text-text-tertiary mt-1.5">
                Recommended: 12 or more characters.
              </p>
            </div>
            <ToggleRow
              id="sec-special-chars"
              label="Require special characters"
              description="Passwords must include at least one special character."
              checked={form.requireSpecialChars}
              onChange={(checked): void => {
                setForm((prev) => ({ ...prev, requireSpecialChars: checked }));
              }}
            />
          </GlassCardContent>
        </GlassCard>
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck size={15} className="text-text-secondary" />
              <h3 className="text-sm font-semibold text-text">Session & Authentication</h3>
            </div>
          </GlassCardHeader>
          <GlassCardContent className="flex flex-col gap-2 py-5">
            <div className="max-w-xs mb-3">
              <GlassSelect
                label="Session timeout"
                options={SESSION_TIMEOUT_OPTIONS}
                value={form.sessionTimeout}
                onChange={(e): void => {
                  setForm((prev) => ({ ...prev, sessionTimeout: e.target.value }));
                }}
              />
              <p className="text-xs text-text-tertiary mt-1.5">
                Users will be signed out after this period of inactivity.
              </p>
            </div>
            <ToggleRow
              id="sec-2fa"
              label="Two-factor authentication (2FA)"
              description="Require all admin users to verify their identity with a second factor."
              checked={form.twoFactorEnabled}
              onChange={(checked): void => {
                setForm((prev) => ({ ...prev, twoFactorEnabled: checked }));
              }}
            />
          </GlassCardContent>
        </GlassCard>
      </div>
      <div className="mt-4 flex justify-end">
        <GlassButton variant="primary" size="md">
          Save Changes
        </GlassButton>
      </div>
    </GlassTabPanel>
  );
}

/* ── Page ── */

export function SettingsPage(): ReactElement {
  const [activeTab, setActiveTab] = useState<TabId>('general');

  return (
    <div className="space-y-6">
      <p className="text-sm text-text-secondary">
        Manage your organization settings, preferences, and integrations.
      </p>
      <div>
        <GlassTabs
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={(id): void => {
            setActiveTab(id as TabId);
          }}
        />
        {activeTab === 'general' && <GeneralTab />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'integrations' && <IntegrationsTab />}
        {activeTab === 'security' && <SecurityTab />}
      </div>
    </div>
  );
}
